<template>
    <li class="tree-node-container">
        <div class="tree-node-name">
            <TreeNodeToggle :node="node" />

            <template v-if="showEditMode">
                <AutocompleteInput
                    ref="editAutocompleteInput"
                    :auto-focus="true"
                    @confirm="confirmEdit"
                    @cancel="cancelEdit"
                    v-model="editedNodeName"
                    :items="translatedCategories"
                    :results-filter="resultsFilter"
                    :input-class="'is-small'"
                >
                    <template v-slot:after-input>
                        <LoadingButton
                            class="button is-small is-success is-rounded"
                            @click="confirmEdit(['click', true])"
                            :is-loading="editInProgress"
                        >
                            {{ translate("common.ok") }}
                        </LoadingButton>
                        <button class="button is-small is-danger is-rounded" @click="cancelEdit">
                            {{ translate("common.cancel") }}
                        </button>
                    </template>
                    <template v-slot:result-item="{ resultItem }">{{ resultItem }}</template>
                </AutocompleteInput>
            </template>

            <TreeNodeMetadata :node="node" />

            <TreeNodeToolbox :node="node" :breadcrumb="breadcrumb" />
        </div>

        <ol v-if="hasAnyChildren(node.id) && isExpandedNode(node.id)">
            <Sortable :list="childrenForId" item-key="id" @update="onChangedOrder" :options="dragOptions">
                <template v-slot:item="{ element: childNode }">
                    <TreeNode :node="childNode" :key="childNode.id" :breadcrumb="[...breadcrumb, node.id]" />
                </template>
            </Sortable>

            <li class="tree-node-container">
                <div class="tree-node-name">
                    <AutocompleteInput
                        ref="addAutocompleteInput"
                        :auto-focus="true"
                        @confirm="confirmAddition"
                        @cancel="cancelAddition"
                        v-if="showAddMode"
                        :items="translatedCategories"
                        v-model="addedNodeName"
                        :results-filter="resultsFilter"
                        :input-class="'is-small'"
                    >
                        <template v-slot:after-input>
                            <LoadingButton
                                class="button is-small is-success is-rounded"
                                :is-loading="addingInProgress"
                                @click="confirmAddition(['click'])"
                            >
                                {{ translate("common.ok") }}
                            </LoadingButton>
                            <button class="button is-small is-danger is-rounded" @click="cancelAddition">
                                {{ translate("common.cancel") }}
                            </button>
                        </template>
                        <template v-slot:result-item="{ resultItem }">
                            {{ resultItem }}
                        </template>
                    </AutocompleteInput>
                    <button
                        v-else
                        class="button is-small is-rounded is-light"
                        :disabled="categoriesModificationsEnabled"
                        @click="enableAddingMode"
                    >
                        {{ translate("page.category_manager.add_category") }}
                    </button>
                </div>
            </li>
        </ol>
    </li>
</template>

<script setup lang="ts">
    import { ref, computed, watch, onMounted } from "vue";
    import AutocompleteInput from "./AutocompleteInput.vue";
    import { translate } from "../Common/translations";
    import { ENABLE_EDIT_MODE, eventBus } from "./eventBus";
    import categoriesApi from "../Api/Categories/categories";
    import { handleError } from "../Common/errorHandler";
    import TreeNodeToolbox from "./TreeNodeToolbox.vue";
    import TreeNodeMetadata from "./TreeNodeMetadata.vue";
    import TreeNodeToggle from "./TreeNodeToggle.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import { KEY_TAB, KEY_TAB_SHIFT } from "../Common/consts";
    import { Sortable } from "sortablejs-vue3";
    import { useCategoriesManagerStore } from "./categoriesManagerStore";
    import type { CategoryNode } from "../Api/Categories/categories";
    import type { ComponentExposed } from "vue-component-type-helpers";

    const props = defineProps<{
        node: CategoryNode;
        breadcrumb: number[];
    }>();

    const store = useCategoriesManagerStore();

    const addedNodeName = ref("");
    const addedNodeKey = ref("");
    const addingInProgress = ref(false);
    const editedNodeKey = ref("");
    const editInProgress = ref(false);
    const editAutocompleteInput = ref<ComponentExposed<typeof AutocompleteInput> | null>(null);
    const addAutocompleteInput = ref<ComponentExposed<typeof AutocompleteInput> | null>(null);

    const dragOptions = {
        animation: 150,
        scroll: true,
        forceFallback: true,
        bubbleScroll: true,
        filter: "a, button, input, .toolbox",
        preventOnFilter: false,
    };

    const editedNodeName = computed({
        get() {
            return store.editedNodeName;
        },
        set(value: string) {
            store.editedNodeName = value;
        },
    });

    const hasAnyChildren = computed(() => store.hasAnyChildren);
    const isExpandedNode = computed(() => store.isExpandedNode);

    const childrenForId = computed(() => {
        return store.getChildrenForId(props.node.id);
    });

    const translatedNodeName = computed(() => {
        return translate(store.categoryTranslationKeys[props.node.name] || props.node.name, true);
    });

    const translatedCategories = computed(() => {
        const keys = Object.keys(store.categoryTranslationKeys);
        return keys.map(key => translate(store.categoryTranslationKeys[key]));
    });

    const translationsToKeys = computed(() => {
        const keys = Object.keys(store.categoryTranslationKeys);
        const result: Record<string, string> = {};
        keys.forEach(key => {
            result[translate(store.categoryTranslationKeys[key])] = key;
        });
        return result;
    });

    const showEditMode = computed(() => {
        return store.editModeEnabled === props.node.id || props.node.extendingNode;
    });

    const showAddMode = computed(() => {
        return store.addingModeEnabled === props.node.id;
    });

    const categoriesModificationsEnabled = computed(() => store.categoriesModificationsEnabled);

    watch(
        () => addedNodeName.value,
        () => {
            const trimmed = addedNodeName.value.trim();
            addedNodeKey.value = translationsToKeys.value[trimmed] || trimmed;
        },
    );

    watch(
        () => editedNodeName.value,
        () => {
            const trimmed = editedNodeName.value.trim();
            editedNodeKey.value = translationsToKeys.value[trimmed] || trimmed;
        },
    );

    onMounted(() => {
        editInProgress.value = false;
        addingInProgress.value = false;

        eventBus.$on(ENABLE_EDIT_MODE, (nodeId: number) => {
            if (props.node.id === nodeId) {
                enableEditMode();
            }
        });
    });

    function resultsFilter(item: string, search: string) {
        if (!item) {
            return false;
        }

        return item.toLowerCase().indexOf(search.toLowerCase()) > -1;
    }

    function enableEditMode(targetNodeId: number | null = null, targetName: string | null = null) {
        store.categoriesModificationsEnabled = true;

        if (targetNodeId === null && targetName === null) {
            store.editModeEnabled = props.node.id;
            editedNodeName.value = translatedNodeName.value;
        } else {
            store.editModeEnabled = targetNodeId!;
            editedNodeName.value = translate(store.categoryTranslationKeys[targetName!] || targetName!, true);
        }
    }

    function disableEditMode() {
        store.categoriesModificationsEnabled = false;
        store.editModeEnabled = 0;
        editedNodeName.value = "";
    }

    function confirmEdit([sourceKey, changed]: [string, boolean]) {
        editInProgress.value = true;

        if (props.node.extendingNode) {
            let newParamId = 0;

            return categoriesApi
                .addParameter({ eventId: store.eventId, parentId: props.node.parentId!, name: editedNodeKey.value })
                .then(id => {
                    newParamId = id;
                    return store.refreshCategories();
                })
                .then(() => {
                    disableEditMode();
                })
                .catch(handleError)
                .finally(() => {
                    editInProgress.value = false;
                    handleTabPress(newParamId, props.node.parentId!, sourceKey);
                });
        }

        if (!changed) {
            disableEditMode();
            editInProgress.value = false;
            handleTabPress(props.node.id, props.node.parentId!, sourceKey);
            return;
        }

        return categoriesApi
            .updateParameter(store.eventId, props.node.id, editedNodeKey.value)
            .then(() => {
                return store.refreshCategories();
            })
            .then(() => {
                disableEditMode();
            })
            .catch(handleError)
            .finally(() => {
                editInProgress.value = false;
                handleTabPress(props.node.id, props.node.parentId!, sourceKey);
            });
    }

    function cancelEdit() {
        disableEditMode();
        if (props.node.extendingNode) {
            return store.refreshCategories();
        }
        return Promise.resolve();
    }

    function enableAddingMode() {
        store.categoriesModificationsEnabled = true;
        store.addingModeEnabled = props.node.id;
        addedNodeName.value = "";
    }

    function disableAddingMode() {
        store.categoriesModificationsEnabled = false;
        store.addingModeEnabled = 0;
        addedNodeName.value = "";
    }

    function confirmAddition([sourceKey]: [string, boolean?]) {
        addingInProgress.value = true;

        let newParamId = 0;

        if (addedNodeKey.value.length === 0 && sourceKey === KEY_TAB_SHIFT) {
            disableAddingMode();
            addingInProgress.value = false;
            //Having null here will point to last child instead previous item for last child.
            handleTabPress(null, props.node.id, sourceKey);
            return;
        }

        return categoriesApi
            .addParameter({ eventId: store.eventId, parentId: props.node.id, name: addedNodeKey.value })
            .then(id => {
                newParamId = id;
                return store.refreshCategories();
            })
            .then(() => {
                disableAddingMode();
            })
            .catch(err => {
                addAutocompleteInput.value?.selectText();
                handleError(err);
            })
            .finally(() => {
                addingInProgress.value = false;
                handleTabPress(newParamId, props.node.id, sourceKey);
            });
    }

    function cancelAddition() {
        disableAddingMode();
    }

    function onChangedOrder(event: { oldIndex: number; newIndex: number }) {
        if (event.oldIndex === event.newIndex) {
            return;
        }

        const movedChild = childrenForId.value[event.oldIndex];

        store.moveNodeChild({
            nodeId: props.node.id,
            oldIndex: event.oldIndex,
            newIndex: event.newIndex,
        });

        return categoriesApi
            .updateOrderOfParameter(store.eventId, movedChild.id, event.oldIndex, event.newIndex)
            .then(() => {
                return store.refreshCategories();
            })
            .catch(handleError);
    }

    function handleTabPress(nodeId: number | null, parentId: number, sourceKey: string) {
        if (!parentId) {
            return;
        }

        if (sourceKey === KEY_TAB) {
            const nextNode = store.getNextSiblingForNodeId(nodeId!, parentId);

            if (nextNode) {
                enableEditMode(nextNode.id, nextNode.name);
            } else {
                store.categoriesModificationsEnabled = true;
                store.addingModeEnabled = parentId;
                addedNodeName.value = "";
            }
        } else if (sourceKey === KEY_TAB_SHIFT) {
            const nextNode = store.getPrevSiblingForNodeId(nodeId, parentId);

            if (nextNode) {
                enableEditMode(nextNode.id, nextNode.name);
            }
        }
    }
</script>
